/**
 * translationDb.js
 *
 * IndexedDB utility for the durable tier of the translation cache.
 * translationStore holds the synchronous Map that tr() reads; this module is what
 * survives a reload, so a returning user does not have to re-download the whole
 * dictionary. Chosen over sessionStorage because the dictionary is expected to
 * reach hundreds of thousands of entries once tag/field/content translation lands
 * (see the usage=TAG note in TranslationDictionary.graphql).
 *
 * Schema:
 *   Store "entries" → { affiliationOrganization, usage, targetLanguage,
 *                       sourceText, translatedText, updatedAt }
 *     keyPath  : ['affiliationOrganization','usage','targetLanguage','sourceText']
 *                compound so AUTO / FIELD_LABEL / TAG / ONDEMAND share one store
 *                without a DB version bump when they arrive.
 *     index    : "byScopeUpdatedAt" on
 *                ['affiliationOrganization','usage','targetLanguage','updatedAt']
 *                → delta sync now, LRU eviction later.
 *   Store "meta"    → { key, syncedAt, entryCount }
 *     key      : org, usage and language joined by META_KEY_SEPARATOR.
 *     syncedAt : the high-water mark handed to the delta query.
 *     plus one org-level row (usage CACHE_RESET) holding `resetAt`, the last
 *     Corporation.translationCacheResetAt seen; a change wipes the org's cache.
 *
 * Mirrors the openDB / withStore / requestToPromise idiom of callRecordingDB.js,
 * with one deliberate difference: every public function here is FAIL-SAFE. This
 * cache sits on the render path and the spec requires never blank/never error, so
 * a failure degrades to memory-only translation instead of rejecting.
 */

const DB_NAME = 'SdeskTranslationDB';
const DB_VERSION = 2;
const STORE_ENTRIES = 'entries';
const STORE_META = 'meta';
const INDEX_SCOPE_UPDATED_AT = 'byScopeUpdatedAt';

// Kept local so this module does not depend on the Constant bundle.
const USAGE_ONDEMAND = 'ONDEMAND';

// NUL as an escape, not a literal byte - cannot occur in an org id, usage or
// language tag, and keeps this file plain ASCII for grep/tooling.
const META_KEY_SEPARATOR = String.fromCharCode(0);

// Usage slot of the org-level meta row that remembers Corporation.translationCacheResetAt.
const META_CACHE_RESET = 'CACHE_RESET';

let dbInstance = null;

/** IndexedDB is missing in private mode, old Safari and SSR/test environments. */
export const isAvailable = () => typeof indexedDB !== 'undefined' && indexedDB !== null;

const openDB = () =>
    new Promise((resolve, reject) => {
        // Reuse existing connection if still open
        if (dbInstance) {
            try {
                if (dbInstance.name === DB_NAME) {
                    return resolve(dbInstance);
                }
            } catch {
                dbInstance = null;
            }
        }

        const req = indexedDB.open(DB_NAME, DB_VERSION);

        req.onupgradeneeded = e => {
            const db = e.target.result;

            // v1 keyed entries on sourceText, which cannot hold a 350k-char
            // article. v2 keys on a generic entryKey. Nothing has shipped, so
            // the old store is simply recreated rather than migrated.
            if (db.objectStoreNames.contains(STORE_ENTRIES) && e.oldVersion < 2) {
                db.deleteObjectStore(STORE_ENTRIES);
            }

            if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
                const entryStore = db.createObjectStore(STORE_ENTRIES, {
                    keyPath: ['affiliationOrganization', 'usage', 'targetLanguage', 'entryKey']
                });
                entryStore.createIndex(
                    INDEX_SCOPE_UPDATED_AT,
                    ['affiliationOrganization', 'usage', 'targetLanguage', 'updatedAt'],
                    { unique: false }
                );
            }

            if (!db.objectStoreNames.contains(STORE_META)) {
                db.createObjectStore(STORE_META, { keyPath: 'key' });
            }
        };

        req.onsuccess = () => {
            dbInstance = req.result;
            // Reset cached instance if browser closes the connection
            dbInstance.onclose = () => {
                dbInstance = null;
            };
            dbInstance.onversionchange = () => {
                dbInstance.close();
                dbInstance = null;
            };
            resolve(dbInstance);
        };
        req.onerror = () => reject(req.error);
    });

const withStore = async (storeName, mode = 'readonly') => {
    const db = await openDB();
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    return { db, tx, store };
};

const requestToPromise = req =>
    new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });

const txToPromise = tx =>
    new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });

const buildMetaKey = (affiliationOrganization, usage, targetLanguage) =>
    [affiliationOrganization, usage, targetLanguage].join(META_KEY_SEPARATOR);

/**
 * Key range covering one (org, usage, language) scope.
 * An empty array sorts after every string in IndexedDB's key ordering, so it is
 * the standard upper bound for "any sourceText".
 */
const scopeRange = (affiliationOrganization, usage, targetLanguage) =>
    IDBKeyRange.bound(
        [affiliationOrganization, usage, targetLanguage],
        [affiliationOrganization, usage, targetLanguage, []]
    );

// ─── Entry operations ───────────────────────────

/**
 * Reads every cached translation for one scope - the bulk hydrate on startup.
 * @returns {Promise<Array>} rows, or [] on any failure.
 */
export const readScope = async (affiliationOrganization, usage, targetLanguage) => {
    if (!isAvailable() || !affiliationOrganization || !usage || !targetLanguage) {
        return [];
    }

    try {
        const { store } = await withStore(STORE_ENTRIES, 'readonly');
        const rows = await requestToPromise(
            store.getAll(scopeRange(affiliationOrganization, usage, targetLanguage))
        );
        return Array.isArray(rows) ? rows : [];
    } catch (err) {
        return [];
    }
};

/**
 * Writes a batch of translations for one scope in a single transaction.
 * @param {Object} pairs - { [sourceText]: translatedText }
 * @returns {Promise<number>} how many rows were written, 0 on any failure.
 */
export const putScopeEntries = async (
    affiliationOrganization,
    usage,
    targetLanguage,
    pairs,
    updatedAt = new Date().toISOString()
) => {
    if (!isAvailable() || !affiliationOrganization || !usage || !targetLanguage) {
        return 0;
    }
    if (!pairs || typeof pairs !== 'object') {
        return 0;
    }

    const sourceTexts = Object.keys(pairs).filter(text => text && pairs[text]);
    if (sourceTexts.length === 0) {
        return 0;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        sourceTexts.forEach(sourceText => {
            store.put({
                affiliationOrganization,
                usage,
                targetLanguage,
                // Short usages key on the text itself; on-demand content keys on
                // its source id (see putContentEntry) because a 350k-char article
                // cannot be a key.
                entryKey: sourceText,
                sourceText,
                translatedText: pairs[sourceText],
                updatedAt
            });
        });
        await txToPromise(tx);
        return sourceTexts.length;
    } catch (err) {
        return 0;
    }
};

/**
 * Looks up specific source texts. Not needed by the AUTO bulk-load path; this is
 * the entry point for the lazy per-id usages (TAG / FIELD_LABEL) later on.
 * @returns {Promise<Object>} { [sourceText]: translatedText } for the ones found.
 */
export const getMany = async (affiliationOrganization, usage, targetLanguage, sourceTexts) => {
    if (!isAvailable() || !Array.isArray(sourceTexts) || sourceTexts.length === 0) {
        return {};
    }

    try {
        const { store } = await withStore(STORE_ENTRIES, 'readonly');
        const found = {};
        await Promise.all(
            sourceTexts.map(async sourceText => {
                if (!sourceText) return;
                const row = await requestToPromise(
                    store.get([affiliationOrganization, usage, targetLanguage, sourceText])
                );
                /* entryKey === sourceText for the short usages, so the tuple above
                   is already the right key. */
                if (row?.translatedText) {
                    found[sourceText] = row.translatedText;
                }
            })
        );
        return found;
    } catch (err) {
        return {};
    }
};

export const deleteScope = async (affiliationOrganization, usage, targetLanguage) => {
    if (!isAvailable() || !affiliationOrganization || !usage || !targetLanguage) {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        store.delete(scopeRange(affiliationOrganization, usage, targetLanguage));
        await txToPromise(tx);
        await deleteMeta(affiliationOrganization, usage, targetLanguage);
        return true;
    } catch (err) {
        return false;
    }
};

/** Deletes one cached translation - used right after it was deleted on 翻訳管理. */
export const deleteEntry = async (affiliationOrganization, usage, targetLanguage, sourceText) => {
    if (!isAvailable() || !affiliationOrganization || !usage || !targetLanguage || !sourceText) {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        store.delete([affiliationOrganization, usage, targetLanguage, sourceText]);
        await txToPromise(tx);
        return true;
    } catch (err) {
        return false;
    }
};

/**
 * Drops every on-demand content translation (usage = ONDEMAND) across all
 * organizations and languages. Used when the on-demand cache must be reset
 * regardless of the current org - e.g. switching project or logging out, where
 * the active organization may already be gone from localStorage.
 * @returns {Promise<number>} how many rows were deleted.
 */
export const clearAllOnDemand = async () => {
    if (!isAvailable()) {
        return 0;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        let deleted = 0;

        await new Promise((resolve, reject) => {
            const req = store.openCursor();
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(true);
                    return;
                }
                if (cursor.value?.usage === USAGE_ONDEMAND) {
                    cursor.delete();
                    deleted += 1;
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });

        await txToPromise(tx);
        return deleted;
    } catch (err) {
        return 0;
    }
};

/**
 * Drops every cached translation of one organization - all usages, all languages -
 * together with its meta rows, so the next bootstrap does a full load.
 */
export const deleteOrganization = async affiliationOrganization => {
    if (!isAvailable() || !affiliationOrganization) {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        // The org is the first key component, so [org] .. [org, []] spans all of it.
        store.delete(IDBKeyRange.bound([affiliationOrganization], [affiliationOrganization, []]));
        await txToPromise(tx);
        return await deleteMetaRows(row => row?.affiliationOrganization === affiliationOrganization);
    } catch (err) {
        return false;
    }
};

/**
 * Drops every scope that does not belong to the current organization.
 * Without this the database grows without bound for anyone who switches orgs.
 * @returns {Promise<number>} how many rows were deleted.
 */
export const deleteOtherOrganizations = async currentOrganization => {
    if (!isAvailable() || !currentOrganization) {
        return 0;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        let deleted = 0;

        await new Promise((resolve, reject) => {
            const req = store.openCursor();
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(true);
                    return;
                }
                if (cursor.value?.affiliationOrganization !== currentOrganization) {
                    cursor.delete();
                    deleted += 1;
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });

        await txToPromise(tx);
        await deleteMetaForOtherOrganizations(currentOrganization);
        return deleted;
    } catch (err) {
        return 0;
    }
};

// ─── Meta operations ────────────────────────────

/**
 * @returns {Promise<Object|null>} { key, syncedAt, entryCount } or null.
 */
export const readMeta = async (affiliationOrganization, usage, targetLanguage) => {
    if (!isAvailable() || !affiliationOrganization || !usage || !targetLanguage) {
        return null;
    }

    try {
        const { store } = await withStore(STORE_META, 'readonly');
        const row = await requestToPromise(
            store.get(buildMetaKey(affiliationOrganization, usage, targetLanguage))
        );
        return row || null;
    } catch (err) {
        return null;
    }
};

export const writeMeta = async (
    affiliationOrganization,
    usage,
    targetLanguage,
    { syncedAt, entryCount } = {}
) => {
    if (!isAvailable() || !affiliationOrganization || !usage || !targetLanguage) {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_META, 'readwrite');
        store.put({
            key: buildMetaKey(affiliationOrganization, usage, targetLanguage),
            affiliationOrganization,
            usage,
            targetLanguage,
            // null = nothing synced yet; never fall back to the client clock (it may be skewed).
            syncedAt: syncedAt || null,
            entryCount: typeof entryCount === 'number' ? entryCount : 0
        });
        await txToPromise(tx);
        return true;
    } catch (err) {
        return false;
    }
};

export const deleteMeta = async (affiliationOrganization, usage, targetLanguage) => {
    if (!isAvailable()) {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_META, 'readwrite');
        store.delete(buildMetaKey(affiliationOrganization, usage, targetLanguage));
        await txToPromise(tx);
        return true;
    } catch (err) {
        return false;
    }
};

/**
 * The Corporation.translationCacheResetAt value this browser last synced against.
 * @returns {Promise<{resetAt: string|null}|null>} null when no marker was ever written
 *   (e.g. a cache built before the marker existed) - the caller must treat it as stale.
 */
export const readCacheResetMarker = async affiliationOrganization => {
    const row = await readMeta(affiliationOrganization, META_CACHE_RESET, META_CACHE_RESET);
    return row ? { resetAt: row.resetAt || null } : null;
};

export const writeCacheResetMarker = async (affiliationOrganization, resetAt) => {
    if (!isAvailable() || !affiliationOrganization) {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_META, 'readwrite');
        store.put({
            key: buildMetaKey(affiliationOrganization, META_CACHE_RESET, META_CACHE_RESET),
            // Kept so deleteMetaForOtherOrganizations cleans this row up too.
            affiliationOrganization,
            usage: META_CACHE_RESET,
            resetAt: resetAt || null
        });
        await txToPromise(tx);
        return true;
    } catch (err) {
        return false;
    }
};

/**
 * Deletes every meta row matching a predicate. Shared helper so callers can
 * express which rows to drop (by org, by usage, etc.) without duplicating the
 * cursor plumbing.
 * @param {(row: Object) => boolean} predicate - returns true for rows to delete.
 * @returns {Promise<boolean>} true on success.
 */
const deleteMetaRows = async predicate => {
    if (!isAvailable() || typeof predicate !== 'function') {
        return false;
    }

    try {
        const { tx, store } = await withStore(STORE_META, 'readwrite');
        await new Promise((resolve, reject) => {
            const req = store.openCursor();
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(true);
                    return;
                }
                if (predicate(cursor.value)) {
                    cursor.delete();
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });
        await txToPromise(tx);
        return true;
    } catch (err) {
        return false;
    }
};

const deleteMetaForOtherOrganizations = async currentOrganization =>
    deleteMetaRows(row => row?.affiliationOrganization !== currentOrganization);

// ---- On-demand content ----------------------------------------------------

/**
 * Reads one cached on-demand translation.
 *
 * `fingerprint` is how an edited source is detected: pass the source object's
 * updatedAt (or a content hash when there is none). A mismatch is reported as a
 * miss so the caller re-translates rather than showing a stale body.
 *
 * @returns {Promise<string|null>} the translated content, or null.
 */
export const readContentEntry = async (
    affiliationOrganization,
    targetLanguage,
    sourceId,
    fingerprint
) => {
    if (!isAvailable() || !affiliationOrganization || !targetLanguage || !sourceId) {
        return null;
    }

    try {
        const { store } = await withStore(STORE_ENTRIES, 'readonly');
        const row = await requestToPromise(
            store.get([affiliationOrganization, USAGE_ONDEMAND, targetLanguage, sourceId])
        );
        if (!row?.translatedText) return null;
        if (fingerprint && row.sourceFingerprint !== fingerprint) return null;
        return row.translatedText;
    } catch (err) {
        return null;
    }
};

/**
 * Caches one on-demand translation. Keyed by sourceId, not by the body, because
 * a rich-text field can reach 350k characters.
 */
export const putContentEntry = async ({
    affiliationOrganization,
    targetLanguage,
    sourceId,
    sourceType,
    translatedText,
    sourceFingerprint,
    updatedAt = new Date().toISOString()
}) => {
    if (!isAvailable() || !affiliationOrganization || !targetLanguage || !sourceId) {
        return false;
    }
    if (!translatedText) return false;

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        store.put({
            affiliationOrganization,
            usage: USAGE_ONDEMAND,
            targetLanguage,
            entryKey: sourceId,
            sourceId,
            sourceType,
            translatedText,
            sourceFingerprint,
            updatedAt
        });
        await txToPromise(tx);
        return true;
    } catch (err) {
        return false;
    }
};

// ---- Eviction --------------------------------------------------------------

/**
 * Caps one scope, deleting the oldest rows first.
 *
 * Necessary because the cache is otherwise unbounded: a production org has 500k
 * tags (~85MB if all cached) and a single translated article can be ~1MB.
 *
 * Ordering is by `updatedAt` via byScopeUpdatedAt, i.e. FIFO rather than true
 * LRU - approximating it that way avoids a write on every cache read.
 *
 * @returns {Promise<number>} how many rows were deleted.
 */
export const enforceScopeLimit = async (affiliationOrganization, usage, targetLanguage, limit) => {
    if (!isAvailable() || !limit || limit < 1) {
        return 0;
    }

    try {
        const { tx, store } = await withStore(STORE_ENTRIES, 'readwrite');
        const index = store.index(INDEX_SCOPE_UPDATED_AT);
        const range = IDBKeyRange.bound(
            [affiliationOrganization, usage, targetLanguage],
            [affiliationOrganization, usage, targetLanguage, []]
        );

        const total = await requestToPromise(index.count(range));
        const excess = total - limit;
        if (excess <= 0) {
            return 0;
        }

        let deleted = 0;
        await new Promise((resolve, reject) => {
            // Ascending on updatedAt: the cursor starts at the oldest row.
            const req = index.openCursor(range);
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor || deleted >= excess) {
                    resolve(true);
                    return;
                }
                cursor.delete();
                deleted += 1;
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });

        await txToPromise(tx);
        return deleted;
    } catch (err) {
        return 0;
    }
};

/**
 * Rough guard against filling the origin's storage quota.
 * @returns {Promise<boolean>} true when it is safe to keep caching.
 */
export const hasStorageHeadroom = async (threshold = 0.9) => {
    try {
        if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
            // No Storage API - assume there is room; the caps still apply.
            return true;
        }
        const { usage, quota } = await navigator.storage.estimate();
        if (!quota) return true;
        return usage / quota < threshold;
    } catch (err) {
        return true;
    }
};

/** Test seam: forces the next call to reopen the connection. */
export const resetConnection = () => {
    try {
        dbInstance?.close();
    } catch {
        // already closed
    }
    dbInstance = null;
};

const translationDb = {
    isAvailable,
    readScope,
    putScopeEntries,
    getMany,
    readContentEntry,
    putContentEntry,
    clearAllOnDemand,
    enforceScopeLimit,
    hasStorageHeadroom,
    deleteScope,
    deleteEntry,
    deleteOrganization,
    deleteOtherOrganizations,
    readMeta,
    writeMeta,
    deleteMeta,
    readCacheResetMarker,
    writeCacheResetMarker,
    resetConnection
};

export default translationDb;
